import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { batch } from 'react-redux';
import { analysisSelector } from '../../redux/selectors/analyses';
import { GET_ANALYSIS } from '../../utils/apollo/analysis';
import {
  completeExecutingAnalysis,
  setExecutingAnalysis,
} from '../../redux/slices/executingAnalyses';
import {
  AnalysisStatus,
  IAnalysisQuery,
  IAnalysisQueryVariables,
} from '../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../utils/types';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { populateAnalysisThunk } from '../../redux/slices/analyses';
import { executingAnalysesSelector } from '../../redux/selectors/executingAnalyses';
import { ExecutingChartLoader } from '../Chart';
import ExecutionHistoryLoader from './ExecutionHistoryLoader';
import ExecutionHistoryReport from './ExecutionHistoryReport';
import { reportViewed } from '../../utils/analytics/events/report/events';
import { useAnalytics } from '../../utils/analytics/useAnalytics';
import useVisitAnalysisEffect from './utils/useVisitAnalysisEffect';
import toastMessage from '../../utils/toastMessage';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface IProps {
  builderId: string;
}

const Container = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  padding-bottom: 200px;
  border-top: 1px solid ${theme.colors.border};
`;

export default ({ builderId }: IProps) => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const executingAnalyses = useAppSelector(executingAnalysesSelector);
  const executionInfo = executingAnalyses[builderId];
  const currentAnalysisMemoizedSelector = useMemo(analysisSelector, []);
  const currentAnalysis = useAppSelector((state) =>
    currentAnalysisMemoizedSelector(state, builderId),
  );

  // Record analysis visit for History
  useVisitAnalysisEffect(builderId);

  const onQueryCompleted = ({ analysis }: IAnalysisQuery) => {
    assertNotNil(analysis);
    const status = analysis.status;

    batch(() => {
      // If exists, manually complete analysis in executing slice
      if (status === AnalysisStatus.Success && executingAnalyses[analysis.id]) {
        dispatch(
          completeExecutingAnalysis({
            analysisId: analysis.id,
          }),
        );
      }

      if (
        (status === AnalysisStatus.Running ||
          status === AnalysisStatus.Pending) &&
        !executingAnalyses[analysis.id]
      ) {
        // special case: if load analysis from history, but not finish on kobol side.
        // For example, user refresh the page. we should continue on the refetch
        dispatch(
          setExecutingAnalysis({
            analysisId: analysis.id,
            executedAt: analysis.executedAt,
            estimatedDuration: analysis.estimatedDuration ?? 0,
          }),
        );
        return;
      }

      // regular load process
      dispatch(populateAnalysisThunk({ analysis }));
    });

    if (status === AnalysisStatus.Failed) {
      toastMessage({
        message: `Execution of ${analysis.display} report Failed.`,
        type: 'error',
      });
    }

    // Notify user that there is no data to compare (per config)
    if (
      status === AnalysisStatus.Success &&
      analysis.__typename === 'Expression' &&
      analysis.comparisonData &&
      analysis.comparisonData.isEmpty
    ) {
      toastMessage({
        message:
          'No data available for comparison. ' +
          'Please adjust the comparison dates and try again.',
        type: 'error',
      });
    }

    track(reportViewed(analysis));
  };

  const { loading: isAnalysisLoading } = useQuery<
    IAnalysisQuery,
    IAnalysisQueryVariables
  >(GET_ANALYSIS, {
    variables: {
      analysisId: builderId,
    },
    onCompleted: onQueryCompleted,
    notifyOnNetworkStatusChange: true,
  });

  // When executing, we will show a special loader component
  // with animated Kubit logo, progress bar, and Cancel Query
  if (Boolean(executionInfo)) {
    return (
      <Container>
        <ExecutingChartLoader
          analysisId={builderId}
          executionInfo={executionInfo}
        />
      </Container>
    );
  }

  // if analysis is loading, display loading placeholder.
  // !currentAnalysisId means that analysis hasn't been yet dispatched to redux
  if (isAnalysisLoading || !currentAnalysis) {
    return (
      <Container>
        <ExecutionHistoryLoader analysisId={builderId} />
      </Container>
    );
  }

  return (
    <div>
      <ExecutionHistoryReport analysisId={builderId} />
    </div>
  );
};
